/* Banking v2.0.0 role sections — rendered with the existing preview primitives.
 * Two headings supplied by the content package:
 *   current    → "How This Banking Career Works with an AI-Agent Workforce"
 *   automation → "How an AI-Agent Workforce Could Take Over This Banking Role and Eliminate the Position"
 */
import React from 'react';
import { Bot, TrendingDown } from 'lucide-react';
import { Section, InfoCard, Bullets } from '../preview-ui/PreviewPrimitives';

const EXPOSURE = {
  higher: { label: 'Higher exposure', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
  mixed: { label: 'Mixed exposure', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  lower_for_full_workflow: { label: 'Lower for the full workflow', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
};

export const ExposureChip = ({ level, testId }) => {
  const e = EXPOSURE[level];
  if (!e) return null;
  return (
    <span data-testid={testId} className={`inline-flex items-center text-xs font-bold px-3 py-1 rounded-full border ${e.cls}`}>
      {e.label}
    </span>
  );
};

export const NumberedSteps = ({ items, tone = 'sky' }) => {
  const tones = {
    sky: { box: 'bg-sky-50 border-sky-200', num: 'text-sky-700' },
    amber: { box: 'bg-amber-50 border-amber-200', num: 'text-amber-700' },
  };
  const t = tones[tone] || tones.sky;
  return (
    <ol className="space-y-2">
      {(items || []).map((s, i) => (
        <li key={i} className={`rounded-2xl border ${t.box} p-4 flex items-start gap-3`}>
          <span className={`${t.num} font-extrabold text-base shrink-0`}>{String(i + 1).padStart(2, '0')}</span>
          <p className="text-[15px] text-gray-800 leading-relaxed">{s}</p>
        </li>
      ))}
    </ol>
  );
};

export const AgentWorkforceSection = ({ role, labels }) => {
  const ai = role.ai_agent_integration || {};
  const stack = role.ai_stack_for_this_family || {};
  const hasAny = role.current_ai_assistance?.length || role.agent_workflow?.length || role.controls_and_unresolved_work?.length || Object.keys(ai).length;
  if (!hasAny) return null;
  return (
    <Section id="ai-agents" icon={Bot} title={labels?.current || 'How This Banking Career Works with an AI-Agent Workforce'} accent="purple">
      <div className="space-y-4" data-testid="bank-preview-agent-workforce">
        {role.current_ai_assistance?.length > 0 && (
          <div data-testid="bank-preview-current-ai">
            <p className="text-[11px] uppercase font-bold tracking-[0.18em] text-violet-800 mb-2">Current capability — what AI and agents can already do here</p>
            <Bullets items={role.current_ai_assistance} tone="purple" />
          </div>
        )}
        {role.agent_workflow?.length > 0 && (
          <div data-testid="bank-preview-agent-workflow">
            <p className="text-[11px] uppercase font-bold tracking-[0.18em] text-sky-800 mb-2">The connected agent workflow — illustrative; actual deployment varies</p>
            <NumberedSteps items={role.agent_workflow} tone="sky" />
          </div>
        )}
        {role.controls_and_unresolved_work?.length > 0 && (
          <InfoCard label="Controls and unresolved work" tone="cream">
            <div data-testid="bank-preview-controls">
              <Bullets items={role.controls_and_unresolved_work} tone="amber" />
            </div>
          </InfoCard>
        )}
        <div className="grid md:grid-cols-2 gap-3">
          {ai.how_to_work_with_agents && <InfoCard label="How to work with agents" tone="violet">{ai.how_to_work_with_agents}</InfoCard>}
          {ai.agent_human_loop && <InfoCard label="Agent ↔ human loop" tone="mint">{ai.agent_human_loop}</InfoCard>}
        </div>
        {Array.isArray(stack.learn) && stack.learn.length > 0 && (
          <InfoCard label="AI stack for this family — what to learn" tone="violet">
            <div data-testid="bank-preview-ai-stack">
              <Bullets items={stack.learn} tone="purple" />
              {stack.note && <p className="mt-3 text-sm text-gray-700">{stack.note}</p>}
            </div>
          </InfoCard>
        )}
      </div>
    </Section>
  );
};

export const AutomationOutlookSection = ({ role, labels }) => {
  const out = role.automation_outlook || {};
  const risk = role.ai_risk_and_staying_relevant || {};
  if (!out.position_elimination_scenario && !risk.stay_relevant) return null;
  const level = out.exposure?.level || risk.exposure_label;
  return (
    <Section id="automation" icon={TrendingDown} title={out.heading || labels?.automation || 'How an AI-Agent Workforce Could Take Over This Banking Role and Eliminate the Position'} accent="rose">
      <div className="space-y-4" data-testid="bank-preview-automation">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center text-xs font-bold px-3 py-1 rounded-full border bg-slate-100 text-slate-800 border-slate-200" data-testid="bank-preview-prediction-badge">
            {labels?.prediction_badge || 'Conditional scenario — timing and outcome uncertain'}
          </span>
          <ExposureChip level={level} testId="bank-preview-exposure" />
          {out.evidence_type && <span className="text-xs text-gray-500 font-medium">Evidence: {out.evidence_type}</span>}
        </div>
        {out.exposure?.rationale && <InfoCard label="Why this exposure label" tone="rose">{out.exposure.rationale}</InfoCard>}
        {out.position_elimination_scenario && (
          <InfoCard label="How the position could shrink, merge or disappear" tone="cream">
            <span data-testid="bank-preview-elimination-scenario">{out.position_elimination_scenario}</span>
          </InfoCard>
        )}
        <div className="grid md:grid-cols-2 gap-3">
          {out.employment_outcome && <InfoCard label="Possible employment outcome" tone="sky">{out.employment_outcome}</InfoCard>}
          {out.time_limit && <InfoCard label="Timing" tone="teal">{out.time_limit}</InfoCard>}
        </div>
        {out.watch_signals?.length > 0 && (
          <div data-testid="bank-preview-watch-signals">
            <p className="text-[11px] uppercase font-bold tracking-[0.18em] text-rose-800 mb-2">Observable adoption signals to watch</p>
            <Bullets items={out.watch_signals} tone="rose" />
          </div>
        )}
        {risk.stay_relevant && <InfoCard label="How to stay relevant" tone="mint">{risk.stay_relevant}</InfoCard>}
        {risk.assessment_type && <p className="text-xs text-gray-500">Assessment type: {risk.assessment_type}. No role is labelled permanently safe.</p>}
      </div>
    </Section>
  );
};
